/**
 * <pd-youtube-lightbox> — click-to-load YouTube facade, playing either in a
 * modal or in place.
 *
 * Why a facade: a bare YouTube <iframe> pulls ~1MB of player JS on page load
 * even when nobody presses play, which costs LCP/INP on merchandising pages.
 * Here the resting state is just YouTube's own thumbnail image; the iframe is
 * created on click and destroyed on close (which is also what stops audio).
 *
 * The player starts on open: the facade's play button is the visitor's play
 * intent, so the modal does not ask them to press play again. Nothing loads
 * or plays before that click.
 *
 * Two playback modes, chosen per section by the youtube_playback setting and
 * passed in as data-playback:
 *
 *   popup (default) — the player opens in a modal over the page, sized to the
 *     viewport. Use when the video should be the whole focus.
 *   inline — the player replaces the facade in the section's own box. The box
 *     is set to 16:9 for this media type (.pd-image-text-banner__image-wrapper
 *     --youtube), so the player fits it exactly — nothing cropped, no bands.
 *     Chosen when the design wants the video to sit in the layout rather than
 *     over it.
 *
 * Markup contract (see sections/pd-image-text-banner.liquid):
 *   <pd-youtube-lightbox data-video-id="ID" data-video-title="…"
 *                        data-playback="popup|inline">
 *     <button class="pd-youtube-lightbox__trigger">…thumbnail…</button>
 *   </pd-youtube-lightbox>
 *
 * `enablejsapi=1` is set on the embed so GTM's built-in YouTube trigger picks
 * the player up — see app/javascript/pd-tracking.js, which deliberately skips
 * youtube.com iframes to avoid double-firing video_* events.
 */
(function () {
  if (customElements.get('pd-youtube-lightbox')) return;

  // autoplay is ON: pressing the facade's play button IS the visitor's play
  // intent, so neither mode makes them press play a second time on YouTube's
  // own poster. Nothing loads or plays before that click.
  function embedSrc(videoId) {
    return (
      'https://www.youtube.com/embed/' +
      encodeURIComponent(videoId) +
      '?autoplay=1&rel=0&playsinline=1&modestbranding=1&enablejsapi=1'
    );
  }

  function buildPlayer(videoId, title, className) {
    const iframe = document.createElement('iframe');
    iframe.className = className;
    iframe.title = title;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    iframe.setAttribute('frameborder', '0');
    iframe.src = embedSrc(videoId);
    return iframe;
  }

  class PdYoutubeLightbox extends HTMLElement {
    connectedCallback() {
      if (this.dataset.pdWired) return;
      this.dataset.pdWired = '1';

      this.trigger = this.querySelector('.pd-youtube-lightbox__trigger');
      if (!this.trigger) return;

      this.onTriggerClick =
        this.dataset.playback === 'inline'
          ? this.openInline.bind(this)
          : this.open.bind(this);
      this.trigger.addEventListener('click', this.onTriggerClick);
    }

    disconnectedCallback() {
      this.close();
      this.closeInline();
    }

    /**
     * Inline: swap the facade for the player in the section's own box. There is
     * no close affordance by design — the video is part of the layout now, and
     * YouTube's own pause is the stop control. Removing the iframe (on
     * disconnect, e.g. a theme-editor re-render) is what stops audio.
     */
    openInline() {
      const videoId = this.dataset.videoId;
      if (!videoId || this.inlineFrame) return;

      const frame = document.createElement('div');
      frame.className = 'pd-youtube-lightbox__inline';
      const iframe = buildPlayer(
        videoId,
        this.dataset.videoTitle || 'YouTube video',
        'pd-youtube-lightbox__inline-iframe'
      );
      frame.appendChild(iframe);

      this.inlineFrame = frame;
      // Hide rather than remove so closeInline can restore the facade.
      this.trigger.hidden = true;
      this.appendChild(frame);
      // The button the visitor just pressed is gone; move focus to the player
      // so keyboard users aren't dropped back at the top of the document.
      iframe.focus();
    }

    closeInline() {
      if (!this.inlineFrame) return;
      this.inlineFrame.remove();
      this.inlineFrame = null;
      if (this.trigger) this.trigger.hidden = false;
    }

    open() {
      const videoId = this.dataset.videoId;
      if (!videoId || this.overlay) return;

      const title = this.dataset.videoTitle || 'YouTube video';

      const overlay = document.createElement('div');
      overlay.className = 'pd-youtube-lightbox__overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', title);

      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'pd-youtube-lightbox__close';
      closeButton.setAttribute('aria-label', this.dataset.closeLabel || 'Close video');
      closeButton.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
        '<path d="M5 5 19 19M19 5 5 19" stroke="currentColor" stroke-width="2" ' +
        'stroke-linecap="round" fill="none"/></svg>';

      const frame = document.createElement('div');
      frame.className = 'pd-youtube-lightbox__frame';

      const iframe = buildPlayer(videoId, title, 'pd-youtube-lightbox__iframe');

      frame.appendChild(iframe);
      overlay.appendChild(closeButton);
      overlay.appendChild(frame);

      this.overlay = overlay;
      this.closeButton = closeButton;
      this.iframe = iframe;
      this.previouslyFocused = document.activeElement;

      this.onOverlayClick = (event) => {
        if (!frame.contains(event.target)) this.close();
      };
      this.onKeydown = (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.key !== 'Tab') return;
        // Two focusable stops: close button and the player. A true trap can't
        // span the iframe boundary, so we only guarantee the wrap on our side.
        if (event.shiftKey && document.activeElement === closeButton) {
          event.preventDefault();
          iframe.focus();
        } else if (!event.shiftKey && document.activeElement === iframe) {
          event.preventDefault();
          closeButton.focus();
        }
      };

      overlay.addEventListener('click', this.onOverlayClick);
      document.addEventListener('keydown', this.onKeydown);
      closeButton.addEventListener('click', () => this.close());

      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      document.body.appendChild(overlay);
      closeButton.focus();
    }

    close() {
      if (!this.overlay) return;

      document.removeEventListener('keydown', this.onKeydown);
      this.overlay.removeEventListener('click', this.onOverlayClick);
      // Removing the iframe is what actually stops playback and audio.
      this.overlay.remove();
      this.overlay = null;
      this.iframe = null;
      this.closeButton = null;

      document.body.style.overflow = this.previousBodyOverflow || '';

      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.previouslyFocused.focus();
      } else if (this.trigger) {
        this.trigger.focus();
      }
      this.previouslyFocused = null;
    }
  }

  customElements.define('pd-youtube-lightbox', PdYoutubeLightbox);
})();
